import { Component, EventEmitter, Inject, Injector, Input, OnDestroy, OnInit, Output, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { IEntity } from '@interfaces/entity';
import { EntityService } from '@services/entity/entity.service';
import { TableColumn } from '@models/tableColumn';
import { TableFunctionsService } from '@services/table/table-functions.service';
import { FilesService } from '@services/files/files.service';

import { ActivatedRoute, Router } from '@angular/router';
import { NgbdSortableHeader, SortEvent } from 'src/app/directives/sortable.directive';
import { TableColumnOptions } from '@models/tableColumnOptions';
import { TableOptions } from '@models/tableOptions';
import { Table } from '@models/table';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit, OnDestroy {

  public routerSubscription: Subscription;

  @Inject(Router)
  public router: Router;

  @Inject(ActivatedRoute)
  public activatedRoute: ActivatedRoute;

  public entities$: Observable<IEntity[]>;

  @Input() set table(table: Table) {
    this.tableFunctions = table.tableFunctions;
    this.tableFunctions.matches = table.matchFunction;

    this.entityService = table.entityService;
    this.tableColumns = table.tableColumns;
    this.tableOptions = table.tableOptions;
    this.actionsTemplate = table.actionsTemplate;

    this.initializeData(table.tableData);
  }

  @Output() sorted = new EventEmitter<string>();

  entityService: EntityService;
  tableColumns: TableColumn[];
  tableOptions: TableOptions;
  actionsTemplate: TemplateRef<any>;

  public tableFunctions: TableFunctionsService;

  public isAnyCheckBoxClicked: boolean;
  public isCheckboxAllClicked: boolean = false;
  public fileExists: boolean;


  @ViewChildren(NgbdSortableHeader) headers: QueryList<NgbdSortableHeader>;

  @ViewChild('defaultColumnTemplate') defaultColumnTemplate: TemplateRef<any>;
  @ViewChild('linkColumnTemplate') linkColumnTemplate: TemplateRef<any>;
  @ViewChild('htmlColumnTemplate') htmlColumnTemplate: TemplateRef<any>;

  constructor(injector: Injector, private filesService: FilesService) {
    this.tableFunctions = injector.get(TableFunctionsService);
    this.router = injector.get(Router);
    this.activatedRoute = injector.get(ActivatedRoute);
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
  }

  initializeData(data: any) {
    if (this.tableOptions?.showNumbering) {
      var data = this.addNumbering(data);
    }

    this.tableFunctions.entities = data;
    this.tableFunctions.setupFilter();

    this.entities$ = this.tableFunctions.entities$;
  }

  addNumbering(data: any) {
    for (var i = 0; i < data.length; i++) {
      data[i].index = i + 1;
    }
    return data;
  }

  checkIfFileExists(entity: any) {
    var fileName = 'signedMeterValues/' + entity.id + '-signedMeterValue.xml';

    this.filesService.fileExists(fileName).subscribe(result => {
      this.fileExists = result;
    })
  }

  getModelPicture(entity: any) {
    if (this.tableOptions?.hideModelPicture) {
      return 'background-image:none;';
    }
    let image = 'background-image:none;';
    if (entity.model?.pictureUri != null) {
      image = 'background-image:url(' + this.resolveProperty(entity, 'model.pictureUri', null) + ');';
    }
    return image;
  }

  getClasses(entity: any) {
    let classes = ''
    if (this.tableOptions?.rowRouterLink) {
      classes += 'linkTableRow'
    }
    if (this.resolveProperty(entity, 'manufacturer.name', null) != null) {
      classes += ' manufacturer-' + this.resolveProperty(entity, 'manufacturer.name', null).toLowerCase()
    }
    if (this.resolveProperty(entity, 'MeterStart', null) != null) {
      this.checkIfFileExists(entity);
      if (this.fileExists) {
        classes += ' signed '
      }
    }
    return classes
  }

  resolveProperty(entity: IEntity, property: string, options: TableColumnOptions) {
    if (options) {
      return this.tableFunctions.resolveProperty(entity, property, options);
    } else {
      return this.tableFunctions.resolveProperty(entity, property);
    }
  }

  onSort({ column, direction }: SortEvent) {
    this.headers.forEach(header => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });

    this.tableFunctions.sortColumn = column;
    this.tableFunctions.sortDirection = direction;
    this.sorted.emit(column);
    this.tableFunctions.page = 1;
  }

  determineHeaderSortClass(columnProperty: string) {
    var sortClass = "align-middle sortable pe-4";
    if (columnProperty == this.tableFunctions.sortColumn) {
      return sortClass + " " + this.tableFunctions.sortDirection;
    }
    return sortClass;
  }

  checkboxClicked(entity) {
    if (this.checkIfSelected(entity)) {
      const index = this.tableFunctions.selectedEntities.findIndex(x => x.getPrimaryKey() == entity.getPrimaryKey());
      this.tableFunctions.selectedEntities.splice(index, 1);
      if (this.tableFunctions.selectedEntities.length >= 1) {
        this.isAnyCheckBoxClicked = true;

      } else {
        this.isAnyCheckBoxClicked = false;
      }
    }
    else {
      this.tableFunctions.selectedEntities.push(entity);
    }
    this.checkIfAllSelected();
  }

  checkboxAllClicked(checked: boolean) {
    this.tableFunctions.entities$.pipe(take(1)).subscribe(currentEntities => {
      if (checked) {
        this.isCheckboxAllClicked = true;
        this.tableFunctions.selectedEntities = [];
        currentEntities.forEach(e => {
          this.tableFunctions.selectedEntities.push(e);
        })
      } else {
        this.isCheckboxAllClicked = false;
        this.tableFunctions.selectedEntities = [];
        this.isAnyCheckBoxClicked = false;
      }
      this.checkIfAllSelected();
    })
  }

  checkIfSelected(entity) {
    this.checkIfAllSelected();
    if (this.tableFunctions.selectedEntities.includes(entity)) {
      return true;
    }
    return false;
  }

  checkIfAllSelected() {
    this.tableFunctions.entities$.pipe(take(1)).subscribe(currentEntities => {
      if (this.tableFunctions.selectedEntities.length < currentEntities.length) {
        this.isCheckboxAllClicked = false;
      } else {
        this.isCheckboxAllClicked = true;
      }
    })
  }

  determineRouterLink(routerLink: string, entity) {
    var propertyToReplaceRegex = /{([^}]+)}/g;
    var propertyToReplaceMatch = routerLink.match(propertyToReplaceRegex);
    var propertyToReplace = propertyToReplaceMatch[0].replace(/[{}]/g, '');
    var replaceRegex = propertyToReplaceMatch[0];

    return routerLink.replace(replaceRegex, entity[propertyToReplace]);
  }
}
